/**
 * Pure sign-in gate decision behind `useRequireAuth`.
 *
 * Lives in its own module with no imports: `useRequireAuth` pulls in
 * SignInModalContext → SignInModal → Mixpanel → clerkConfig, which constructs a
 * Clerk instance at import time and throws without a publishable key. Tests get
 * the decision without that chain.
 *
 * The experimentFreeGenerate bypass stays inert until `isLDidentified`, the same rule as
 * resolveSignInCopy / resolvePlanTier and for the same reason: before identify resolves,
 * withLDProvider evaluates against an anonymous context that has no mpDeviceId, and a
 * context missing the bucketBy attribute hashes to bucket 0.0 — so every such visitor is
 * served the FIRST variation rather than a random one (see the identify effect in
 * routes/Base/base.jsx). Reading it early could open the menus for a control-arm visitor
 * and stamp their menu Mixpanel events — this experiment's readout — against the wrong
 * context. Gating until identify lands keeps the pre-experiment behavior in that window.
 *
 * @param {Object} params
 * @param {boolean} params.isLoaded - Clerk has resolved the session.
 * @param {boolean} params.isSignedIn - User is authenticated.
 * @param {boolean} [params.allowAnonymousWhenFreeGenerate] - Caller opted into the bypass.
 * @param {boolean} [params.isLDidentified] - LD has identified this visitor's context.
 * @param {boolean} [params.experimentFreeGenerate] - Anonymous generation is enabled.
 * @returns {boolean} Whether the sign-in modal should be shown instead of running the action.
 */
export const shouldGateSignIn = ({
  isLoaded,
  isSignedIn,
  allowAnonymousWhenFreeGenerate = false,
  isLDidentified = false,
  experimentFreeGenerate = false,
}) =>
  isLoaded &&
  !isSignedIn &&
  !(allowAnonymousWhenFreeGenerate && isLDidentified && experimentFreeGenerate);
